import React, { useState, useEffect } from "react";
import "./Range.css";
import range1 from "../assetsprogressive/range1.png";
import range2 from "../assetsprogressive/range2.png";
import range3 from "../assetsprogressive/range3.png";
import range4 from "../assetsprogressive/range4.png";
import range5 from "../assetsprogressive/range5.png";

const rangeCards = [
  {
    id: 1,
    image: range1,
    bg: "#fee0ca",
    title: "Interactive & Conversion-Focused Web Designs",
    description:
      "Designs that feel like native apps and drive results. Optimized for engagement, intuitive navigation, and higher conversions, helping businesses increase user interaction and achieve up to 30% better conversion rates.",
  },
  {
    id: 2,
    image: range2,
    bg: "#eadff8",
    title: "Lightning-Fast Performance & Load Optimization",
    description:
      "PWAs that load in under 2 seconds, even on slow networks. Reduced bounce rates, faster session times, and seamless offline access ensure your users stay engaged and satisfied.",
  },
  {
    id: 3,
    image: range3,
    bg: "#fad3fb",
    title: "Offline-Ready Functionality & Seamless User Access",
    description:
      "Enable uninterrupted experiences anywhere, anytime. Offline support, smart caching, and background sync let users browse, transact, and interact without connectivity, improving retention and overall engagement by up to 40%.",
  },
  {
    id: 4,
    image: range4,
    bg: "#cbdbf2",
    title: "Push Notifications & Personalized Engagement",
    description:
      "Increase retention and repeat visits with targeted push notifications. Real-time alerts, promotions, and behavioral triggers turn casual visitors into loyal users, driving measurable engagement and revenue growth.",
  },
  {
    id: 5,
    image: range5,
    bg: "#fbd7cd",
    title: "PWA Migration & Enterprise-Ready Integrations",
    description:
      "Convert existing websites into enterprise-grade PWAs without downtime. Seamless API, CRM, and third-party integrations ensure high performance, scalability, and a measurable impact on conversions and user satisfaction.",
  },
];

export default function Range() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isCarousel, setIsCarousel] = useState(false);
  const [slidesPerView, setSlidesPerView] = useState(1);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setIsCarousel(true);
        setSlidesPerView(1);
      } else if (width < 1024) {
        setIsCarousel(true);
        setSlidesPerView(2);
      } else {
        setIsCarousel(false);
        setCurrentSlide(0);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxSlide = Math.ceil(rangeCards.length / slidesPerView) - 1;

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === maxSlide ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? maxSlide : prev - 1));
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) nextSlide();
    if (touchStart - touchEnd < -50) prevSlide();
  };

  const getTransformValue = () => {
    if (slidesPerView === 1) {
      return `translateX(-${currentSlide * 100}%)`;
    } else {
      const slideWidthPercentage = 50;
      const gapInPixels = 20; 
      return `translateX(calc(-${currentSlide * slideWidthPercentage}% - ${currentSlide * gapInPixels}px))`;
    }
  };

  return (
    <section id="progressive-range" className="progressive-wrapper">
      <div className="range-container">
        <div className="top">
          <h2 className="heading">
            Get Secure, Offline-Ready PWAs for Modern Users
          </h2>
          <p className="subheading">
            Create smooth, dependable, and user-friendly digital experiences
            with our progressive web app development services. Your users enjoy
            offline access, app-like speed, and seamless interactions, while
            your business benefits from higher engagement, better conversions,
            and performance that truly stands out.
          </p>
        </div>

        <div className="bottom">
          {!isCarousel ? (
            <div className="cards">
              {rangeCards.map((card) => (
                <div
                  key={card.id}
                  className="card"
                  style={{ backgroundColor: card.bg }}
                >
                  <img
                    src={card.image}
                    alt={card.title}
                    className="card-image"
                  />
                  <h3 className="card-title">{card.title}</h3>
                  <p className="card-description">{card.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="carousel-wrapper">
              <div className="carousel-container">
                <button
                  className="carousel-button"
                  onClick={prevSlide}
                  aria-label="Previous slide"
                >
                  ←
                </button>
                <div className="carousel-viewport">
                  <div
                    className="carousel-track"
                    style={{
                      transform: getTransformValue(),
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    {rangeCards.map((card) => (
                      <div key={card.id} className="carousel-slide">
                        <div
                          className="card"
                          style={{ backgroundColor: card.bg }}
                        >
                          <img
                            src={card.image}
                            alt={card.title}
                            className="card-image"
                          />
                          <h3 className="card-title">{card.title}</h3>
                          <p className="card-description">{card.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  className="carousel-button"
                  onClick={nextSlide}
                  aria-label="Next slide"
                >
                  →
                </button>
              </div>
              <div className="dots-container">
                {Array.from({ length: maxSlide + 1 }).map((_, i) => (
                  <button
                    key={i}
                    className={`dot ${i === currentSlide ? "active" : ""}`}
                    onClick={() => setCurrentSlide(i)}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}